// src/pages/DeepCrawl.jsx
import { useState, useEffect } from 'react';
import { useGraphQL } from '../graphql';
import { StatusBadge } from '../components/Badge';
import Modal from '../components/Modal';
import {
    MagnifyingGlassIcon,
    GlobeAltIcon,
    ChartBarIcon,
    ClockIcon,
    CheckCircleIcon,
    XCircleIcon,
    ExclamationTriangleIcon,
    ArrowPathIcon,
    TrashIcon,
    DocumentMagnifyingGlassIcon,
    ServerStackIcon,
    SignalIcon,
    FunnelIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    PlayIcon,
    EyeIcon,
    CodeBracketIcon
} from '@heroicons/react/24/outline';

const DeepCrawl = () => {
    const { executeQuery, executeMutation, queries, mutations, loading } = useGraphQL();

    // State
    const [activeTab, setActiveTab] = useState('stats');
    const [stats, setStats] = useState(null);
    const [siteConfigs, setSiteConfigs] = useState([]);
    const [siteConfigsTotal, setSiteConfigsTotal] = useState(0);
    const [telemetry, setTelemetry] = useState([]);
    const [telemetryTotal, setTelemetryTotal] = useState(0);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [error, setError] = useState(null);
    const [domainStatsMap, setDomainStatsMap] = useState({}); // domain -> {tenderCount, successRate, ...}
    const [configSortBy, setConfigSortBy] = useState('default'); // 'default', 'tenderCount', 'priority'
    const [companies, setCompanies] = useState([]); // For company selector in batch mode

    // Filters
    const [configFilters, setConfigFilters] = useState({ active: null, expiredOnly: false });
    const [telemetryFilters, setTelemetryFilters] = useState({ successOnly: false, failureOnly: false });
    const [configPage, setConfigPage] = useState(0);
    const [telemetryPage, setTelemetryPage] = useState(0);
    const pageSize = 20;

    // Modals
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCleanupModal, setShowCleanupModal] = useState(false);
    const [showDeepCrawlModal, setShowDeepCrawlModal] = useState(false);
    const [showConfigDetailModal, setShowConfigDetailModal] = useState(false);
    const [configToDelete, setConfigToDelete] = useState(null);
    const [configToView, setConfigToView] = useState(null);
    const [expandedTelemetryIds, setExpandedTelemetryIds] = useState(new Set());
    const [cleanupDays, setCleanupDays] = useState(30);
    const [deepCrawlParams, setDeepCrawlParams] = useState({
        tenderId: '',
        favoritesOnly: false,
        minRating: '',
        companyId: '',
        limit: 100,
        missingDocumentsOnly: false
    });
    const [deepCrawlMode, setDeepCrawlMode] = useState('single'); // 'single' | 'batch'

    useEffect(() => {
        loadData();
        loadCompanies();
    }, []);

    const loadCompanies = async () => {
        try {
            const data = await executeQuery(queries.getCompanies(), { limit: 100, offset: 0 });
            setCompanies(data.adminCompanies || []);
        } catch (err) {
            console.error('Failed to load companies:', err);
        }
    };

    useEffect(() => {
        if (activeTab === 'configs') {
            loadSiteConfigs();
        } else if (activeTab === 'telemetry' && selectedConfig) {
            loadTelemetry();
        }
    }, [activeTab, configPage, telemetryPage, configFilters, telemetryFilters, selectedConfig]);

    const loadData = async () => {
        setError(null);
        try {
            const statsData = await executeQuery(queries.getDeepCrawlStats());
            setStats(statsData.deepCrawlStats);
            setLastRefresh(new Date());

            // Build domain stats map for quick lookup
            const statsMap = {};
            if (statsData.deepCrawlStats?.topDomainsByTenderCount) {
                statsData.deepCrawlStats.topDomainsByTenderCount.forEach(d => {
                    statsMap[d.domain] = d;
                });
            }
            // Also include domains from topDomains that might not be in topDomainsByTenderCount
            if (statsData.deepCrawlStats?.topDomains) {
                statsData.deepCrawlStats.topDomains.forEach(d => {
                    if (!statsMap[d.domain]) {
                        statsMap[d.domain] = d;
                    }
                });
            }
            setDomainStatsMap(statsMap);
        } catch (err) {
            console.error('Failed to load deep crawl stats:', err);
            setError(err.message);
        }
    };

    // Navigate to a specific domain's config
    const navigateToConfig = async (domain) => {
        // Try to find the config in our current list
        let config = siteConfigs.find(c => c.domain === domain);

        if (!config) {
            // Load the config by domain
            try {
                const data = await executeQuery(queries.getDeepCrawlSiteConfig(), { domain });
                config = data.deepCrawlSiteConfig;
            } catch (err) {
                console.error('Failed to load site config:', err);
                setError(`Could not find config for domain: ${domain}`);
                return;
            }
        }

        if (config) {
            setConfigToView(config);
            setShowConfigDetailModal(true);
        } else {
            setError(`No config found for domain: ${domain}`);
        }
    };

    // Navigate to telemetry for a domain
    const navigateToTelemetry = async (domain) => {
        // Try to find the config in our current list
        let config = siteConfigs.find(c => c.domain === domain);

        if (!config) {
            // Load the config by domain
            try {
                const data = await executeQuery(queries.getDeepCrawlSiteConfig(), { domain });
                config = data.deepCrawlSiteConfig;
                if (config) {
                    setSiteConfigs(prev => [...prev, config]);
                }
            } catch (err) {
                console.error('Failed to load site config:', err);
                setError(`Could not find config for domain: ${domain}`);
                return;
            }
        }

        if (config) {
            setSelectedConfig(config);
            setActiveTab('telemetry');
            setTelemetryPage(0);
        } else {
            setError(`No config found for domain: ${domain}`);
        }
    };

    const loadSiteConfigs = async () => {
        setError(null);
        try {
            const data = await executeQuery(queries.getDeepCrawlSiteConfigs(), {
                active: configFilters.active,
                expiredOnly: configFilters.expiredOnly,
                limit: pageSize,
                offset: configPage * pageSize
            });
            setSiteConfigs(data.deepCrawlSiteConfigs?.items || []);
            setSiteConfigsTotal(data.deepCrawlSiteConfigs?.totalCount || 0);
        } catch (err) {
            console.error('Failed to load site configs:', err);
            setError(err.message);
        }
    };

    const loadTelemetry = async () => {
        if (!selectedConfig) return;
        setError(null);
        try {
            const data = await executeQuery(queries.getDeepCrawlTelemetry(), {
                configId: selectedConfig.id,
                successOnly: telemetryFilters.successOnly,
                failureOnly: telemetryFilters.failureOnly,
                limit: pageSize,
                offset: telemetryPage * pageSize
            });
            setTelemetry(data.deepCrawlTelemetry?.items || []);
            setTelemetryTotal(data.deepCrawlTelemetry?.totalCount || 0);
        } catch (err) {
            console.error('Failed to load telemetry:', err);
            setError(err.message);
        }
    };

    const handleDeleteConfig = async () => {
        if (!configToDelete) return;
        try {
            await executeMutation(mutations.deleteDeepCrawlSiteConfig, { domain: configToDelete.domain });
            setShowDeleteModal(false);
            setConfigToDelete(null);
            loadSiteConfigs();
            loadData();
        } catch (err) {
            console.error('Failed to delete config:', err);
            setError(err.message);
        }
    };

    const handleCleanupTelemetry = async () => {
        try {
            const result = await executeMutation(mutations.cleanupDeepCrawlTelemetry, { olderThanDays: cleanupDays });
            setShowCleanupModal(false);
            if (result.cleanupDeepCrawlTelemetry?.success) {
                alert(`Cleaned up ${result.cleanupDeepCrawlTelemetry.deletedCount} telemetry entries`);
            }
            loadData();
        } catch (err) {
            console.error('Failed to cleanup telemetry:', err);
            setError(err.message);
        }
    };

    const handleDeepCrawl = async () => {
        try {
            if (deepCrawlMode === 'single') {
                if (!deepCrawlParams.tenderId.trim()) {
                    alert('Tender ID is required');
                    return;
                }
                const result = await executeMutation(mutations.adminDeepCrawlTender, { tenderId: deepCrawlParams.tenderId });
                if (result.adminDeepCrawlTender?.success) {
                    setShowDeepCrawlModal(false);
                    alert('Deep crawl job created: ' + result.adminDeepCrawlTender.jobId);
                } else {
                    alert('Error: ' + (result.adminDeepCrawlTender?.error || 'Unknown error'));
                }
            } else {
                const result = await executeMutation(mutations.adminDeepCrawlAll, {
                    companyId: deepCrawlParams.companyId || null, // null for all companies
                    favoritesOnly: deepCrawlParams.favoritesOnly || null,
                    minRating: deepCrawlParams.minRating ? parseInt(deepCrawlParams.minRating) : null,
                    limit: deepCrawlParams.limit ? parseInt(deepCrawlParams.limit) : null,
                    missingDocumentsOnly: deepCrawlParams.missingDocumentsOnly || null
                });
                if (result.adminDeepCrawlAll?.success) {
                    setShowDeepCrawlModal(false);
                    alert('Deep crawl batch job created: ' + result.adminDeepCrawlAll.jobId);
                } else {
                    alert('Error: ' + (result.adminDeepCrawlAll?.error || 'Unknown error'));
                }
            }
        } catch (err) {
            console.error('Failed to create deep crawl job:', err);
            setError(err.message);
        }
    };

    const getSuccessRateColor = (rate) => {
        if (rate >= 0.9) return 'text-green-700';
        if (rate >= 0.7) return 'text-yellow-700';
        return 'text-red-700';
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString();
    };

    const formatDuration = (ms) => {
        if (!ms) return '-';
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(2)}s`;
    };

    const toggleTelemetryExpanded = (id) => {
        setExpandedTelemetryIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const formatJson = (obj) => {
        if (!obj) return 'null';
        try {
            return JSON.stringify(obj, null, 2);
        } catch {
            return String(obj);
        }
    };

    // Get tender count for a domain from stats
    const getTenderCount = (domain) => {
        return domainStatsMap[domain]?.tenderCount || 0;
    };

    // Calculate priority score (higher = more urgent to fix)
    // Priority = tender count * (1 - success rate) for configs with low success rate
    const calculatePriority = (config) => {
        const tenderCount = getTenderCount(config.domain);
        const failureRate = 1 - (config.successRate || 0);
        // Only prioritize if success rate is below 90%
        if (config.successRate >= 0.9) return 0;
        return Math.round(tenderCount * failureRate * 100);
    };

    // Sort configs based on selected sort option
    const getSortedConfigs = () => {
        if (configSortBy === 'default') return siteConfigs;

        return [...siteConfigs].sort((a, b) => {
            if (configSortBy === 'tenderCount') {
                return getTenderCount(b.domain) - getTenderCount(a.domain);
            }
            if (configSortBy === 'priority') {
                return calculatePriority(b) - calculatePriority(a);
            }
            return 0;
        });
    };

    const sortedConfigs = getSortedConfigs();

    const totalConfigPages = Math.ceil(siteConfigsTotal / pageSize);
    const totalTelemetryPages = Math.ceil(telemetryTotal / pageSize);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                    <DocumentMagnifyingGlassIcon className="h-8 w-8 text-primary" />
                    <h1 className="text-2xl font-bold text-slate-900">Deep Crawl</h1>
                </div>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setShowDeepCrawlModal(true)}
                        className="px-4 py-2 rounded-lg bg-secondary text-on-accent hover:brightness-110 transition-all duration-150 flex items-center space-x-2"
                    >
                        <PlayIcon className="h-4 w-4" />
                        <span>New Deep Crawl</span>
                    </button>
                    <button
                        onClick={() => setShowCleanupModal(true)}
                        className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150 flex items-center space-x-2"
                    >
                        <TrashIcon className="h-4 w-4" />
                        <span>Cleanup</span>
                    </button>
                    <button
                        onClick={loadData}
                        className="px-4 py-2 rounded-lg bg-primary text-on-accent hover:brightness-110 transition-all duration-150 flex items-center space-x-2"
                        disabled={loading}
                    >
                        <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        <span>Refresh</span>
                    </button>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700">Error: {error}</p>
                </div>
            )}

            {/* Tabs */}
            <div className="border-b border-gray-700">
                <nav className="flex space-x-8">
                    <button
                        onClick={() => setActiveTab('stats')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
 activeTab === 'stats'
 ? 'border-primary text-primary'
 : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
 }`}
                    >
                        <div className="flex items-center space-x-2">
                            <ChartBarIcon className="h-5 w-5" />
                            <span>Statistics</span>
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('configs')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
 activeTab === 'configs'
 ? 'border-primary text-primary'
 : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
 }`}
                    >
                        <div className="flex items-center space-x-2">
                            <ServerStackIcon className="h-5 w-5" />
                            <span>Site Configs</span>
                            {siteConfigsTotal > 0 && (
                                <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full text-xs">{siteConfigsTotal}</span>
                            )}
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('telemetry')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
 activeTab === 'telemetry'
 ? 'border-primary text-primary'
 : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
 }`}
                    >
                        <div className="flex items-center space-x-2">
                            <SignalIcon className="h-5 w-5" />
                            <span>Telemetry</span>
                        </div>
                    </button>
                </nav>
            </div>

            {/* Stats Tab */}
            {activeTab === 'stats' && stats && (
                <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="surface-card p-4">
                            <div className="flex items-center">
                                <div className="bg-blue-100 rounded-lg p-2 mr-3">
                                    <ServerStackIcon className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Total Site Configs</p>
                                    <p className="text-xl font-semibold text-slate-900">{stats.totalSiteConfigs}</p>
                                </div>
                            </div>
                        </div>
                        <div className="surface-card p-4">
                            <div className="flex items-center">
                                <div className="bg-green-100 rounded-lg p-2 mr-3">
                                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Active Configs</p>
                                    <p className="text-xl font-semibold text-slate-900">{stats.activeSiteConfigs}</p>
                                </div>
                            </div>
                        </div>
                        <div className="surface-card p-4">
                            <div className="flex items-center">
                                <div className="bg-yellow-100 rounded-lg p-2 mr-3">
                                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Expired Configs</p>
                                    <p className="text-xl font-semibold text-slate-900">{stats.expiredSiteConfigs}</p>
                                </div>
                            </div>
                        </div>
                        <div className="surface-card p-4">
                            <div className="flex items-center">
                                <div className="bg-purple-100 rounded-lg p-2 mr-3">
                                    <ChartBarIcon className="h-5 w-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Recent Success Rate</p>
                                    <p className={`text-xl font-semibold ${getSuccessRateColor(stats.recentSuccessRate)}`}>
                                        {(stats.recentSuccessRate * 100).toFixed(1)}%
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Secondary Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="surface-card p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Total Telemetry Entries</p>
                                    <p className="text-2xl font-semibold text-slate-900">{stats.totalTelemetryEntries?.toLocaleString()}</p>
                                </div>
                                <SignalIcon className="h-8 w-8 text-slate-300" />
                            </div>
                        </div>
                        <div className="surface-card p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Recent Failures</p>
                                    <p className="text-2xl font-semibold text-red-600">{stats.recentFailureCount}</p>
                                </div>
                                <XCircleIcon className="h-8 w-8 text-red-300" />
                            </div>
                        </div>
                    </div>

                    {/* Priority Domains by Tender Count - Most Important for Fixes */}
                    {stats.topDomainsByTenderCount && stats.topDomainsByTenderCount.length > 0 && (
                        <div className="surface-card border border-secondary/30 p-4">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-2">
                                    <ExclamationTriangleIcon className="h-5 w-5 text-secondary" />
                                    <h3 className="text-lg font-semibold text-slate-900">Priority Domains by Tender Volume</h3>
                                </div>
                                <span className="text-xs text-slate-500 bg-slate-100 rounded px-2 py-1">Click row to investigate</span>
                            </div>
                            <p className="text-sm text-slate-500 mb-4">
                                These domains have the most tenders. Focus on improving success rates here for maximum impact.
                            </p>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-200">
                                    <thead>
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Domain</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tenders</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Success</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Failure</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Success Rate</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200">
                                        {stats.topDomainsByTenderCount.map((domain, idx) => {
                                            const needsAttention = domain.successRate < 0.7 && domain.tenderCount > 10;
                                            return (
                                                <tr key={idx} className={`hover:bg-slate-50 cursor-pointer ${needsAttention ? 'bg-red-50' : ''}`}>
                                                    <td className="px-4 py-3" onClick={() => navigateToConfig(domain.domain)}>
                                                        <div className="flex items-center">
                                                            <GlobeAltIcon className="h-4 w-4 text-slate-400 mr-2" />
                                                            <span className="text-slate-900 font-medium hover:text-primary">{domain.domain}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="text-slate-900 font-semibold">{domain.tenderCount}</span>
                                                    </td>
                                                    <td className="px-4 py-3 text-green-600">{domain.successCount}</td>
                                                    <td className="px-4 py-3 text-red-600">{domain.failureCount}</td>
                                                    <td className="px-4 py-3">
                                                        <span className={getSuccessRateColor(domain.successRate)}>
                                                            {(domain.successRate * 100).toFixed(1)}%
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {needsAttention ? (
                                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                                                <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                                                Needs Fix
                                                            </span>
                                                        ) : domain.successRate >= 0.9 ? (
                                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                                                Healthy
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                                                <ClockIcon className="h-3 w-3 mr-1" />
                                                                Monitor
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => navigateToConfig(domain.domain)}
                                                                className="p-1 text-slate-400 hover:text-slate-700"
                                                                title="View Config"
                                                            >
                                                                <EyeIcon className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => navigateToTelemetry(domain.domain)}
                                                                className="p-1 text-blue-600 hover:text-blue-500"
                                                                title="View Telemetry"
                                                            >
                                                                <SignalIcon className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Top Domains by Activity */}
                    {stats.topDomains && stats.topDomains.length > 0 && (
                        <div className="surface-card p-4">
                            <h3 className="text-lg font-semibold text-slate-900 mb-4">Top Domains by Crawl Activity</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-200">
                                    <thead>
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Domain</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tenders</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Success</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Failure</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Success Rate</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Last Crawled</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200">
                                        {stats.topDomains.map((domain, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50 cursor-pointer">
                                                <td className="px-4 py-3" onClick={() => navigateToConfig(domain.domain)}>
                                                    <div className="flex items-center">
                                                        <GlobeAltIcon className="h-4 w-4 text-slate-400 mr-2" />
                                                        <span className="text-slate-900 font-medium hover:text-primary">{domain.domain}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-slate-600">{domain.tenderCount || '-'}</td>
                                                <td className="px-4 py-3 text-green-600">{domain.successCount}</td>
                                                <td className="px-4 py-3 text-red-600">{domain.failureCount}</td>
                                                <td className="px-4 py-3">
                                                    <span className={getSuccessRateColor(domain.successRate)}>
                                                        {(domain.successRate * 100).toFixed(1)}%
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-slate-500 text-sm">{formatDate(domain.lastCrawled)}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() => navigateToConfig(domain.domain)}
                                                            className="p-1 text-slate-400 hover:text-slate-700"
                                                            title="View Config"
                                                        >
                                                            <EyeIcon className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => navigateToTelemetry(domain.domain)}
                                                            className="p-1 text-blue-600 hover:text-blue-500"
                                                            title="View Telemetry"
                                                        >
                                                            <SignalIcon className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {lastRefresh && (
                        <div className="text-sm text-slate-400 text-center">
                            Last refreshed: {lastRefresh.toLocaleTimeString()}
                        </div>
                    )}
                </div>
            )}

            {/* Site Configs Tab */}
            {activeTab === 'configs' && (
                <div className="space-y-4">
                    {/* Filters and Sorting */}
                    <div className="surface-card p-4 flex flex-wrap gap-4 items-center justify-between">
                        <div className="flex flex-wrap gap-4 items-center">
                            <div className="flex items-center space-x-2">
                                <FunnelIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-slate-500 text-sm">Filters:</span>
                            </div>
                            <select
                                value={configFilters.active === null ? '' : configFilters.active.toString()}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setConfigFilters(prev => ({
                                        ...prev,
                                        active: val === '' ? null : val === 'true'
                                    }));
                                    setConfigPage(0);
                                }}
                                className="text-sm"
                            >
                                <option value="">All Status</option>
                                <option value="true">Active Only</option>
                                <option value="false">Inactive Only</option>
                            </select>
                            <label className="flex items-center space-x-2 text-sm text-slate-600">
                                <input
                                    type="checkbox"
                                    checked={configFilters.expiredOnly}
                                    onChange={(e) => {
                                        setConfigFilters(prev => ({ ...prev, expiredOnly: e.target.checked }));
                                        setConfigPage(0);
                                    }}
                                    className="text-primary focus:ring-primary"
                                />
                                <span>Expired Only</span>
                            </label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <span className="text-slate-500 text-sm">Sort:</span>
                            <select
                                value={configSortBy}
                                onChange={(e) => setConfigSortBy(e.target.value)}
                                className="text-sm"
                            >
                                <option value="default">Default</option>
                                <option value="tenderCount">Tender Count (High to Low)</option>
                                <option value="priority">Fix Priority (Urgent First)</option>
                            </select>
                        </div>
                    </div>

                    {/* Table */}
                    {loading ? (
                        <div className="surface-card p-8 text-center">
                            <ArrowPathIcon className="h-8 w-8 text-primary animate-spin mx-auto mb-2" />
                            <p className="text-slate-500">Loading site configs...</p>
                        </div>
                    ) : siteConfigs.length === 0 ? (
                        <div className="surface-card p-8 text-center">
                            <ServerStackIcon className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                            <p className="text-slate-500">No site configurations found</p>
                        </div>
                    ) : (
                        <div className="surface-card overflow-hidden">
                            <table className="min-w-full divide-y divide-slate-200">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Domain</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tenders</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Success Rate</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Priority</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Expires</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                    {sortedConfigs.map((config) => {
                                        const tenderCount = getTenderCount(config.domain);
                                        const priority = calculatePriority(config);
                                        const needsAttention = config.successRate < 0.7 && tenderCount > 10;
                                        return (
                                            <tr key={config.id} className={`hover:bg-slate-50 ${needsAttention ? 'bg-red-50' : ''}`}>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center">
                                                        <GlobeAltIcon className="h-4 w-4 text-slate-400 mr-2" />
                                                        <div>
                                                            <div className="text-slate-900 font-medium">{config.domain}</div>
                                                            <div className="text-xs text-slate-400 truncate max-w-xs">{config.baseUrl}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {tenderCount > 0 ? (
                                                        <span className={`font-semibold ${tenderCount > 50 ? 'text-slate-900' : tenderCount > 10 ? 'text-slate-600' : 'text-slate-500'}`}>
                                                            {tenderCount}
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-400">-</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-slate-600">{config.structureType || '-'}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center space-x-2">
                                                        <span className={getSuccessRateColor(config.successRate)}>
                                                            {(config.successRate * 100).toFixed(1)}%
                                                        </span>
                                                        <span className="text-xs text-slate-400">
                                                            ({config.successCount}/{config.successCount + config.failureCount})
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {needsAttention ? (
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                                            <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                                            High ({priority})
                                                        </span>
                                                    ) : priority > 0 ? (
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                                            Med ({priority})
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-400">-</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <StatusBadge status={config.active ? 'active' : 'inactive'} />
                                                </td>
                                                <td className="px-4 py-3 text-sm text-slate-500">{formatDate(config.expiresAt)}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() => {
                                                                setConfigToView(config);
                                                                setShowConfigDetailModal(true);
                                                            }}
                                                            className="p-1 text-slate-400 hover:text-slate-700"
                                                            title="View Details"
                                                        >
                                                            <EyeIcon className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setSelectedConfig(config);
                                                                setActiveTab('telemetry');
                                                            }}
                                                            className="p-1 text-blue-600 hover:text-blue-500"
                                                            title="View Telemetry"
                                                        >
                                                            <SignalIcon className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setConfigToDelete(config);
                                                                setShowDeleteModal(true);
                                                            }}
                                                            className="p-1 text-red-600 hover:text-red-500"
                                                            title="Delete Config"
                                                        >
                                                            <TrashIcon className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {totalConfigPages > 1 && (
                        <div className="flex items-center justify-between surface-card p-3">
                            <div className="text-sm text-slate-500">
                                Showing {configPage * pageSize + 1}-{Math.min((configPage + 1) * pageSize, siteConfigsTotal)} of {siteConfigsTotal}
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => setConfigPage(p => Math.max(0, p - 1))}
                                    disabled={configPage === 0}
                                    className="p-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150 disabled:opacity-50"
                                >
                                    <ChevronLeftIcon className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => setConfigPage(p => Math.min(totalConfigPages - 1, p + 1))}
                                    disabled={configPage >= totalConfigPages - 1}
                                    className="p-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150 disabled:opacity-50"
                                >
                                    <ChevronRightIcon className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Telemetry Tab */}
            {activeTab === 'telemetry' && (
                <div className="space-y-4">
                    {/* Config Selector */}
                    <div className="surface-card p-4">
                        <label className="block text-sm font-medium text-slate-500 mb-2">Select Site Config</label>
                        <select
                            value={selectedConfig?.id || ''}
                            onChange={async (e) => {
                                if (!e.target.value) {
                                    setSelectedConfig(null);
                                    return;
                                }
                                // Load configs if not loaded yet
                                if (siteConfigs.length === 0) {
                                    const data = await executeQuery(queries.getDeepCrawlSiteConfigs(), { limit: 100 });
                                    setSiteConfigs(data.deepCrawlSiteConfigs?.items || []);
                                }
                                const config = siteConfigs.find(c => c.id === e.target.value);
                                setSelectedConfig(config);
                                setTelemetryPage(0);
                            }}
                            className="w-full"
                        >
                            <option value="">Select a site config...</option>
                            {siteConfigs.map(config => (
                                <option key={config.id} value={config.id}>{config.domain}</option>
                            ))}
                        </select>
                    </div>

                    {selectedConfig && (
                        <>
                            {/* Filters */}
                            <div className="surface-card p-4 flex flex-wrap gap-4 items-center">
                                <div className="flex items-center space-x-2">
                                    <FunnelIcon className="h-5 w-5 text-slate-400" />
                                    <span className="text-slate-500 text-sm">Filters:</span>
                                </div>
                                <label className="flex items-center space-x-2 text-sm text-slate-600">
                                    <input
                                        type="checkbox"
                                        checked={telemetryFilters.successOnly}
                                        onChange={(e) => {
                                            setTelemetryFilters(prev => ({
                                                ...prev,
                                                successOnly: e.target.checked,
                                                failureOnly: e.target.checked ? false : prev.failureOnly
                                            }));
                                            setTelemetryPage(0);
                                        }}
                                        className="text-primary focus:ring-primary"
                                    />
                                    <span>Success Only</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm text-slate-600">
                                    <input
                                        type="checkbox"
                                        checked={telemetryFilters.failureOnly}
                                        onChange={(e) => {
                                            setTelemetryFilters(prev => ({
                                                ...prev,
                                                failureOnly: e.target.checked,
                                                successOnly: e.target.checked ? false : prev.successOnly
                                            }));
                                            setTelemetryPage(0);
                                        }}
                                        className="text-primary focus:ring-primary"
                                    />
                                    <span>Failure Only</span>
                                </label>
                            </div>

                            {/* Telemetry Table */}
                            {loading ? (
                                <div className="surface-card p-8 text-center">
                                    <ArrowPathIcon className="h-8 w-8 text-primary animate-spin mx-auto mb-2" />
                                    <p className="text-slate-500">Loading telemetry...</p>
                                </div>
                            ) : telemetry.length === 0 ? (
                                <div className="surface-card p-8 text-center">
                                    <SignalIcon className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                                    <p className="text-slate-500">No telemetry entries found</p>
                                </div>
                            ) : (
                                <div className="surface-card overflow-hidden">
                                    <table className="min-w-full divide-y divide-slate-200">
                                        <thead className="bg-slate-50">
                                            <tr>
                                                <th className="px-2 py-3 text-left text-xs font-medium text-slate-500 uppercase w-8"></th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Source URL</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tender</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Docs Found</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Duration</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Created</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200">
                                            {telemetry.map((entry) => {
                                                const isExpanded = expandedTelemetryIds.has(entry.id);
                                                const hasDetails = entry.details && Object.keys(entry.details).length > 0;
                                                return (
                                                    <>
                                                        <tr
                                                            key={entry.id}
                                                            className={`hover:bg-slate-50 ${hasDetails ? 'cursor-pointer' : ''}`}
                                                            onClick={() => hasDetails && toggleTelemetryExpanded(entry.id)}
                                                        >
                                                            <td className="px-2 py-3">
                                                                {hasDetails && (
                                                                    <button className="p-1 text-slate-400 hover:text-slate-700">
                                                                        {isExpanded ? (
                                                                            <ChevronUpIcon className="h-4 w-4" />
                                                                        ) : (
                                                                            <ChevronDownIcon className="h-4 w-4" />
                                                                        )}
                                                                    </button>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                {entry.success ? (
                                                                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                                                ) : (
                                                                    <XCircleIcon className="h-5 w-5 text-red-600" />
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="max-w-xs truncate text-sm text-slate-600" title={entry.sourceUrl}>
                                                                    {entry.sourceUrl}
                                                                </div>
                                                                {entry.errorMessage && (
                                                                    <div className="text-xs text-red-600 truncate max-w-xs" title={entry.errorMessage}>
                                                                        {entry.errorMessage}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-slate-500 font-mono">
                                                                {entry.tenderId ? entry.tenderId.slice(0, 8) + '...' : '-'}
                                                            </td>
                                                            <td className="px-4 py-3 text-slate-900">{entry.documentsFound}</td>
                                                            <td className="px-4 py-3 text-slate-500">{formatDuration(entry.duration)}</td>
                                                            <td className="px-4 py-3 text-sm text-slate-500">{formatDate(entry.createdAt)}</td>
                                                        </tr>
                                                        {isExpanded && hasDetails && (
                                                            <tr key={`${entry.id}-details`} className="bg-slate-50">
                                                                <td colSpan={7} className="px-4 py-3">
                                                                    <div className="space-y-2">
                                                                        <div className="flex items-center space-x-2 text-sm text-slate-500">
                                                                            <CodeBracketIcon className="h-4 w-4" />
                                                                            <span>Telemetry Details</span>
                                                                        </div>
                                                                        <pre className="bg-white border border-slate-200 rounded p-3 text-xs text-slate-600 overflow-x-auto max-h-64">
                                                                            {formatJson(entry.details)}
                                                                        </pre>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Pagination */}
                            {totalTelemetryPages > 1 && (
                                <div className="flex items-center justify-between surface-card p-3">
                                    <div className="text-sm text-slate-500">
                                        Showing {telemetryPage * pageSize + 1}-{Math.min((telemetryPage + 1) * pageSize, telemetryTotal)} of {telemetryTotal}
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => setTelemetryPage(p => Math.max(0, p - 1))}
                                            disabled={telemetryPage === 0}
                                            className="p-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150 disabled:opacity-50"
                                        >
                                            <ChevronLeftIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => setTelemetryPage(p => Math.min(totalTelemetryPages - 1, p + 1))}
                                            disabled={telemetryPage >= totalTelemetryPages - 1}
                                            className="p-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150 disabled:opacity-50"
                                        >
                                            <ChevronRightIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setConfigToDelete(null);
                }}
                title="Delete Site Config"
            >
                <div className="space-y-4">
                    <p className="text-slate-600">
                        Are you sure you want to delete the site configuration for{' '}
                        <span className="font-semibold text-slate-900">{configToDelete?.domain}</span>?
                    </p>
                    <p className="text-sm text-slate-500">
                        This will force a re-analysis on the next deep crawl for this domain.
                    </p>
                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            onClick={() => {
                                setShowDeleteModal(false);
                                setConfigToDelete(null);
                            }}
                            className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDeleteConfig}
                            className="px-4 py-2 rounded-lg bg-red-600 text-on-accent hover:bg-red-500 transition-all duration-150"
                        >
                            Delete
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Cleanup Modal */}
            <Modal
                isOpen={showCleanupModal}
                onClose={() => setShowCleanupModal(false)}
                title="Cleanup Telemetry"
            >
                <div className="space-y-4">
                    <p className="text-slate-600">
                        Delete telemetry entries older than the specified number of days.
                    </p>
                    <div>
                        <label className="block text-sm font-medium text-slate-500 mb-2">Days to Keep</label>
                        <input
                            type="number"
                            min="1"
                            value={cleanupDays}
                            onChange={(e) => setCleanupDays(parseInt(e.target.value) || 30)}
                        />
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            onClick={() => setShowCleanupModal(false)}
                            className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCleanupTelemetry}
                            className="px-4 py-2 rounded-lg bg-red-600 text-on-accent hover:bg-red-500 transition-all duration-150"
                        >
                            Cleanup
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Deep Crawl Modal */}
            <Modal
                isOpen={showDeepCrawlModal}
                onClose={() => setShowDeepCrawlModal(false)}
                title="New Deep Crawl Job"
            >
                <div className="space-y-4">
                    {/* Mode Toggle */}
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setDeepCrawlMode('single')}
                            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-150 ${
 deepCrawlMode === 'single'
 ? 'bg-primary/10 text-primary-dark'
 : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
 }`}
                        >
                            Single Tender
                        </button>
                        <button
                            onClick={() => setDeepCrawlMode('batch')}
                            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-150 ${
 deepCrawlMode === 'batch'
 ? 'bg-primary/10 text-primary-dark'
 : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
 }`}
                        >
                            Batch Crawl
                        </button>
                    </div>

                    {deepCrawlMode === 'single' ? (
                        <div>
                            <label className="block text-sm font-medium text-slate-500 mb-2">Tender ID</label>
                            <input
                                type="text"
                                value={deepCrawlParams.tenderId}
                                onChange={(e) => setDeepCrawlParams(prev => ({ ...prev, tenderId: e.target.value }))}
                                placeholder="Enter tender ID..."
                            />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-500 mb-2">Company (Optional - filter by company)</label>
                                <select
                                    value={deepCrawlParams.companyId}
                                    onChange={(e) => setDeepCrawlParams(prev => ({ ...prev, companyId: e.target.value }))}
                                    className="w-full"
                                >
                                    <option value="">All Companies</option>
                                    {companies.map(company => (
                                        <option key={company.id} value={company.id}>
                                            {company.name}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-slate-400 mt-1">Leave as &quot;All Companies&quot; to crawl across all tenants</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-500 mb-2">Minimum Rating (optional)</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="5"
                                    value={deepCrawlParams.minRating}
                                    onChange={(e) => setDeepCrawlParams(prev => ({ ...prev, minRating: e.target.value }))}
                                    placeholder="1-5"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-500 mb-2">Limit</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="1000"
                                    value={deepCrawlParams.limit}
                                    onChange={(e) => setDeepCrawlParams(prev => ({ ...prev, limit: parseInt(e.target.value) || 100 }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="flex items-center space-x-2 text-sm text-slate-600">
                                    <input
                                        type="checkbox"
                                        checked={deepCrawlParams.favoritesOnly}
                                        onChange={(e) => setDeepCrawlParams(prev => ({ ...prev, favoritesOnly: e.target.checked }))}
                                        className="text-primary focus:ring-primary"
                                    />
                                    <span>Favorites Only</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm text-slate-600">
                                    <input
                                        type="checkbox"
                                        checked={deepCrawlParams.missingDocumentsOnly}
                                        onChange={(e) => setDeepCrawlParams(prev => ({ ...prev, missingDocumentsOnly: e.target.checked }))}
                                        className="text-primary focus:ring-primary"
                                    />
                                    <span>Missing Documents Only</span>
                                </label>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            onClick={() => setShowDeepCrawlModal(false)}
                            className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDeepCrawl}
                            className="px-4 py-2 rounded-lg bg-primary text-on-accent hover:brightness-110 transition-all duration-150"
                        >
                            Start Deep Crawl
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Site Config Detail Modal */}
            <Modal
                isOpen={showConfigDetailModal}
                onClose={() => {
                    setShowConfigDetailModal(false);
                    setConfigToView(null);
                }}
                title="Site Configuration Details"
                size="xl"
            >
                {configToView && (
                    <div className="space-y-6">
                        {/* Priority Banner if config needs attention */}
                        {(() => {
                            const tenderCount = getTenderCount(configToView.domain);
                            const priority = calculatePriority(configToView);
                            const needsAttention = configToView.successRate < 0.7 && tenderCount > 10;
                            if (needsAttention) {
                                return (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                        <div className="flex items-center space-x-3">
                                            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                                            <div>
                                                <div className="text-red-700 font-medium">High Priority Fix Required</div>
                                                <div className="text-red-600 text-sm">
                                                    This domain affects {tenderCount} tenders with only {(configToView.successRate * 100).toFixed(0)}% success rate.
                                                    Priority score: {priority}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })()}

                        {/* Basic Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <span className="text-slate-500 text-sm">Domain</span>
                                <div className="text-slate-900 font-medium">{configToView.domain}</div>
                            </div>
                            <div>
                                <span className="text-slate-500 text-sm">Base URL</span>
                                <div className="text-slate-900 text-sm truncate" title={configToView.baseUrl}>
                                    {configToView.baseUrl}
                                </div>
                            </div>
                            <div>
                                <span className="text-slate-500 text-sm">Tenders Using This Domain</span>
                                <div className="text-slate-900 font-semibold">
                                    {getTenderCount(configToView.domain) || <span className="text-slate-400 font-normal">Not tracked</span>}
                                </div>
                            </div>
                            <div>
                                <span className="text-slate-500 text-sm">Structure Type</span>
                                <div className="text-slate-900">{configToView.structureType || '-'}</div>
                            </div>
                            <div>
                                <span className="text-slate-500 text-sm">Confidence</span>
                                <div className={getSuccessRateColor(configToView.confidence)}>
                                    {(configToView.confidence * 100).toFixed(0)}%
                                </div>
                            </div>
                            <div>
                                <span className="text-slate-500 text-sm">Success Rate</span>
                                <div className={getSuccessRateColor(configToView.successRate)}>
                                    {(configToView.successRate * 100).toFixed(1)}% ({configToView.successCount}/{configToView.successCount + configToView.failureCount})
                                </div>
                            </div>
                            <div>
                                <span className="text-slate-500 text-sm">Status</span>
                                <div>
                                    <StatusBadge status={configToView.active ? 'active' : 'inactive'} />
                                </div>
                            </div>
                        </div>

                        {/* Timestamps */}
                        <div className="border-t border-slate-200 pt-4">
                            <h4 className="text-sm font-medium text-slate-600 mb-3">Timestamps</h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-slate-500">Created:</span>
                                    <span className="text-slate-900 ml-2">{formatDate(configToView.createdAt)}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500">Updated:</span>
                                    <span className="text-slate-900 ml-2">{formatDate(configToView.updatedAt)}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500">Analyzed:</span>
                                    <span className="text-slate-900 ml-2">{formatDate(configToView.analyzedAt)}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500">Expires:</span>
                                    <span className="text-slate-900 ml-2">{formatDate(configToView.expiresAt)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Analysis Data */}
                        {configToView.analysis && (
                            <div className="border-t border-slate-200 pt-4">
                                <h4 className="text-sm font-medium text-slate-600 mb-3 flex items-center">
                                    <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                                    Analysis
                                </h4>
                                <pre className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-600 overflow-x-auto max-h-48">
                                    {formatJson(configToView.analysis)}
                                </pre>
                            </div>
                        )}

                        {/* Extraction Rules */}
                        {configToView.extraction && (
                            <div className="border-t border-slate-200 pt-4">
                                <h4 className="text-sm font-medium text-slate-600 mb-3 flex items-center">
                                    <DocumentMagnifyingGlassIcon className="h-4 w-4 mr-2" />
                                    Extraction Rules
                                </h4>
                                <pre className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-600 overflow-x-auto max-h-48">
                                    {formatJson(configToView.extraction)}
                                </pre>
                            </div>
                        )}

                        {/* LLM Parameters */}
                        {configToView.llmParams && (
                            <div className="border-t border-slate-200 pt-4">
                                <h4 className="text-sm font-medium text-slate-600 mb-3 flex items-center">
                                    <CodeBracketIcon className="h-4 w-4 mr-2" />
                                    LLM Parameters
                                </h4>
                                <pre className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-600 overflow-x-auto max-h-48">
                                    {formatJson(configToView.llmParams)}
                                </pre>
                            </div>
                        )}

                        {/* No advanced data */}
                        {!configToView.analysis && !configToView.extraction && !configToView.llmParams && (
                            <div className="border-t border-slate-200 pt-4">
                                <div className="text-center text-slate-400 py-4">
                                    <CodeBracketIcon className="h-8 w-8 mx-auto mb-2" />
                                    <p>No analysis, extraction, or LLM parameter data available</p>
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="border-t border-slate-200 pt-4 flex justify-between">
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => {
                                        setSelectedConfig(configToView);
                                        setShowConfigDetailModal(false);
                                        setActiveTab('telemetry');
                                        setTelemetryPage(0);
                                    }}
                                    className="px-4 py-2 rounded-lg bg-blue-600 text-on-accent hover:bg-blue-500 transition-all duration-150 flex items-center space-x-2"
                                >
                                    <SignalIcon className="h-4 w-4" />
                                    <span>View Telemetry</span>
                                </button>
                                <button
                                    onClick={() => {
                                        setConfigToDelete(configToView);
                                        setShowConfigDetailModal(false);
                                        setShowDeleteModal(true);
                                    }}
                                    className="px-4 py-2 rounded-lg bg-red-600 text-on-accent hover:bg-red-500 transition-all duration-150 flex items-center space-x-2"
                                >
                                    <TrashIcon className="h-4 w-4" />
                                    <span>Delete Config</span>
                                </button>
                            </div>
                            <button
                                onClick={() => {
                                    setShowConfigDetailModal(false);
                                    setConfigToView(null);
                                }}
                                className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default DeepCrawl;
